import Listener from '~/modules/utils/listener';

type accessStatus = 'uninitialised' | 'requested' | 'accepted' | 'declined';

// The only errors that actually mean the user (or the platform) refused access. Everything else
// - NotFoundError, OverconstrainedError, NotReadableError - is about the requested device, not
// about permission.
const permissionErrorNames = ['NotAllowedError', 'PermissionDeniedError', 'SecurityError'];

const isPermissionError = (e: unknown) =>
  typeof e === 'object' && e !== null && permissionErrorNames.includes((e as { name?: string }).name ?? '');

class UserMediaService extends Listener<[accessStatus]> {
  private status: accessStatus = 'uninitialised';

  public getUserMedia: typeof navigator.mediaDevices.getUserMedia = async (...args) =>
    this.requestAndTrack(() => navigator.mediaDevices.getUserMedia(...args));

  /** Asks for the microphone and lets go of it at once. A probe left open holds the mic, and on
   * phones that allow one capture at a time the mic opened to sing then fails or comes back silent. */
  public requestMicPermission = async () => {
    const stream = await this.getUserMedia({ audio: true });
    stream.getTracks().forEach((track) => track.stop());
  };

  public enumerateDevices: typeof navigator.mediaDevices.enumerateDevices = async (...args) =>
    this.requestAndTrack(() => navigator.mediaDevices.enumerateDevices(...args));

  private requestAndTrack = async <T extends CallableFunction>(fnc: T) => {
    const statusBeforeRequest = this.status;
    try {
      if (this.status !== 'accepted') this.setStatus('requested');
      const result = await fnc();
      this.setStatus('accepted');

      return result;
    } catch (e) {
      // A request pinned to one device fails as soon as that device is gone - unplugged
      // mid-session, or a mic that dropped off the list. Reporting that as 'declined' hides the
      // whole app behind the "access is blocked" screen even though access was granted.
      this.setStatus(isPermissionError(e) ? 'declined' : statusBeforeRequest);
      throw e;
    }
  };

  private setStatus = (newStatus: accessStatus) => {
    this.status = newStatus;

    this.onUpdate(newStatus);
  };

  public getStatus = () => this.status;
}

export default new UserMediaService();
